import { useState, useEffect, useRef, useCallback } from "react";
import { FiNavigation2 } from "react-icons/fi";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

function MapboxRobot(props) {
  const mapContainerRef = useRef(null); // Ref for the map container
  const mapRef = useRef(null); // Ref for the map instance
  const [robotMarker, setRobotMarker] = useState(null);
  const [dropMarker, setDropMarker] = useState(null);
  const [pickupMarker, setPickupMarker] = useState(null);
  const [pickupLocationPopup, setPickupLocationPopup] = useState(null);
  const [dropLocationPopup, setDropLocationPopup] = useState(null);
  const [userControllingMap, setUserControllingMap] = useState(false);

  const isLatitude = (num) => isFinite(num) && Math.abs(num) <= 90;
  const isLongitude = (num) => isFinite(num) && Math.abs(num) <= 180;

  // Function to add geofence polygon
  const addGeofencePolygon = useCallback((map) => {
    console.log("Adding geofence polygon with raw data:", props?.geofenceData);
    
    // Remove any existing geofence layers first
    if (map.getLayer('geofence-stroke')) {
      map.removeLayer('geofence-stroke');
    }
    if (map.getSource('geofence')) {
      map.removeSource('geofence');
    }
    
    if (!props?.geofenceData || !Array.isArray(props.geofenceData) || props.geofenceData.length === 0) {
      console.log("No geofence data available - removing any existing geofence display");
      return;
    }

    // Convert geofence data to GeoJSON polygon coordinates
    const coordinates = [
      props.geofenceData.map(point => [point.lng, point.lat])
    ];

    console.log("Geofence polygon coordinates:", coordinates);

    // Add source
    if (map.getSource('geofence')) {
      map.removeSource('geofence');
    }
    
    map.addSource('geofence', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: coordinates
        },
        properties: {
          name: 'Geofence Boundary'
        }
      }
    });

    // Add stroke layer (solid red border only - no fill)
    map.addLayer({
      id: 'geofence-stroke',
      type: 'line',
      source: 'geofence',
      paint: {
        'line-color': '#ff0000',
        'line-width': 3,
        'line-opacity': 0.8
      }
    });

    console.log("Geofence polygon added successfully");
  }, [props?.geofenceData]);

  useEffect(() => {
    mapboxgl.accessToken =
      "pk.eyJ1IjoibXJsYWtzaHlhZ3VwdGEiLCJhIjoiY2xpZWZlcTJjMGo4ZDNkczBwc2ZmcW1qMyJ9.Kurk7BS04invJcCwS0m4Pg";

    const initializeMap = () => {
      const mapConfig = {
        container: mapContainerRef.current,
        style: "mapbox://styles/mrlakshyagupta/climr57of00le01pgc0jjgfoh",
        zoom: process.env.REACT_APP_MAP_ZOOM || 12,
      };
      
      // Only add bounds if they exist
      if (props?.maxBoundCordinates?.southwest && props?.maxBoundCordinates?.northeast) {
        mapConfig.maxBounds = [
          props?.maxBoundCordinates?.southwest,
          props?.maxBoundCordinates?.northeast
        ];
        console.log("Using geofence bounds:", mapConfig.maxBounds);
      } else {
        console.log("No geofence bounds available, using default map");
      }
      
      const newMap = new mapboxgl.Map(mapConfig);

      mapRef.current = newMap;
      
      // Add debugging for map bounds
      newMap.on('load', () => {
        console.log("Map loaded successfully");
        console.log("Map bounds:", newMap.getBounds());
        console.log("Map center:", newMap.getCenter());
        console.log("Map zoom:", newMap.getZoom());
        
        // Add geofence polygon (will handle no data case internally)
        addGeofencePolygon(newMap);
      });

      const robotMarkerDiv = document.createElement("div");
      robotMarkerDiv.style.width = "35px";
      robotMarkerDiv.style.height = "35px";
      robotMarkerDiv.style.borderRadius = "100%";
      robotMarkerDiv.style.backgroundImage =
        "url(/assets/images/robot-icon-2.svg)";
      robotMarkerDiv.style.backgroundSize = "contain";
      robotMarkerDiv.style.backgroundRepeat = "no-repeat";
      robotMarkerDiv.style.backgroundPosition = "center";
      robotMarkerDiv.style.backgroundColor = "rgba(255, 255, 255, 0.9)";
      robotMarkerDiv.style.border = "3px solid #00A16F";
      robotMarkerDiv.style.boxShadow = "0 0 10px rgba(0, 161, 111, 0.5)";
      
      console.log("Robot marker div created with styles:", robotMarkerDiv.style.cssText);

      const dropMarkerDiv = document.createElement("div");
      dropMarkerDiv.style.width = "35px";
      dropMarkerDiv.style.height = "35px";
      dropMarkerDiv.style.borderRadius = "100%";
      dropMarkerDiv.style.backgroundImage = "url(/assets/images/dropoff.svg)";

      const pickupMarkerDiv = document.createElement("div");
      pickupMarkerDiv.style.width = "35px";
      pickupMarkerDiv.style.height = "35px";
      pickupMarkerDiv.style.borderRadius = "100%";
      pickupMarkerDiv.style.backgroundImage =
        "url(/assets/images/PickUpLocation-icon.svg)";

      let newDropMarker, newPickupMarker;
      if (props?.robotActiveMission?.missionActions?.length) {
        let missionMarkers = Array(
          props?.robotActiveMission?.missionActions.length
        ).fill(new mapboxgl.Marker(dropMarkerDiv), 0);
        missionMarkers.forEach((marker, index) => {
          marker
            .setLngLat([
              props?.robotActiveMission?.missionActions[index]?.longitude || 0,
              props?.robotActiveMission?.missionActions[index]?.latitude || 0,
            ])
            .addTo(newMap)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div class="custom-popup"><p>${props?.robotActiveMission.missionActions[index]?.locationName}</p></div>`
              )
            )
            .setLngLat([
              props?.robotActiveMission.missionActions[index]?.longitude || 0,
              props?.robotActiveMission.missionActions[index]?.latitude || 0,
            ]);
        });
      }
      // Robot marker will be created in the useEffect when robot location data is available
      console.log("Map initialized, waiting for robot location data...");
      if (props?.orderDropLocation) {
        newDropMarker = new mapboxgl.Marker(dropMarkerDiv)
          .setLngLat([0, 0])
          .addTo(newMap);
        const dropPopup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
        });

        dropPopup
          .setLngLat([0, 0])
          .addTo(newMap)
          .setHTML(
            `<div class="custom-popup"><p>${props?.orderDropLocation?.locationName?props?.orderDropLocation?.locationName:"Drop"}</p></div>`
          );
        setDropLocationPopup(dropPopup);
        setDropMarker(newDropMarker);
      }
      if (props?.orderPickupLocation) {
        newPickupMarker = new mapboxgl.Marker(pickupMarkerDiv)
          .setLngLat([0, 0])
          .addTo(newMap);

        const pickupPopup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
        });

        pickupPopup
          .setLngLat([0, 0])
          .addTo(newMap)
          .setHTML(
            `<div class="custom-popup"><p>${props?.orderPickupLocation?.locationName?props?.orderPickupLocation?.locationName:"Pickup"}</p></div>`
          );
        setPickupLocationPopup(pickupPopup);
        setPickupMarker(newPickupMarker);
      }
    };

    if (!mapRef.current) {
      initializeMap();
    }
  }, [mapRef, props, addGeofencePolygon]);

  // Manual recenter function for the button
  const handleRecenter = () => {
    console.log("Manual recenter button clicked");
    console.log("Current robot location:", props?.robotLocation);
    
    if (!mapRef.current) {
      console.log("Cannot recenter: map not ready");
      return;
    }
    
    if (!props?.robotLocation) {
      console.log("Cannot recenter: no robot location data");
      return;
    }

    const robotLng = parseFloat(props?.robotLocation?.longitudeSlam || props?.robotLocation?.longitude || 0);
    const robotLat = parseFloat(props?.robotLocation?.latitudeSlam || props?.robotLocation?.latitude || 0);
    
    console.log("Recentering on robot coordinates:", robotLng, robotLat);
    console.log("Are coordinates valid?", isLongitude(robotLng), isLatitude(robotLat));
    
    if (isLongitude(robotLng) && isLatitude(robotLat)) {
      // Reset user control flag
      setUserControllingMap(false);
      
      // Center on robot with smooth animation
      mapRef.current.flyTo({
        center: [robotLng, robotLat],
        zoom: 15,
        duration: 1000
      });
      
      console.log("Map recentered on robot with flyTo animation");
    } else {
      console.log("Invalid robot coordinates for recentering:", robotLng, robotLat);
    }
  };

  useEffect(() => {
    if (robotMarker) {
      if (props?.robotLocation?.headingSlam || props?.robotLocation?.heading) {
        let rotation = parseFloat(props?.robotLocation?.headingSlam || 0);
        robotMarker.setRotation(rotation).setRotationAlignment("map");
      }
    }

    if (dropMarker) {
      dropMarker
        .setLngLat([
          parseFloat(props?.orderDropLocation?.longitude) || 0,
          parseFloat(props?.orderDropLocation?.latitude) || 0,
        ])
        .setRotationAlignment("map");

      dropLocationPopup
        .setLngLat([
          props?.orderDropLocation?.longitude || 0,
          props?.orderDropLocation?.latitude || 0,
        ])
        .setHTML(
          `<div class="custom-popup"><p>${props?.orderDropLocation?.locationName?props?.orderDropLocation?.locationName:"Drop"}</p></div>`
        );
    }

    if (pickupMarker) {
      pickupMarker
        .setLngLat([
          parseFloat(props?.orderPickupLocation?.longitude) || 0,
          parseFloat(props?.orderPickupLocation?.latitude) || 0,
        ])
        .setRotationAlignment("map");
      pickupLocationPopup
        .setLngLat([
          props?.orderPickupLocation?.longitude || 0,
          props?.orderPickupLocation?.latitude || 0,
        ])
        .setHTML(
          `<div class="custom-popup"><p>${props?.orderPickupLocation?.locationName?props?.orderPickupLocation?.locationName:"Pickup"}</p></div>`
        );
    }

    // Auto-center logic - only run if user hasn't manually controlled the map
    if (
      !userControllingMap &&
      !!Number(process.env.REACT_APP_USER_MAP_CONTROL) &&
      props?.robotLocation &&
      isLatitude(props?.robotLocation?.latitudeSlam || props?.robotLocation?.latitude) &&
      isLongitude(props?.robotLocation?.longitudeSlam || props?.robotLocation?.longitude)
    ) {
      const robotLng = parseFloat(props?.robotLocation?.longitudeSlam || props?.robotLocation?.longitude || 0);
      const robotLat = parseFloat(props?.robotLocation?.latitudeSlam || props?.robotLocation?.latitude || 0);
      
      console.log("Auto-centering map on robot:", robotLng, robotLat);
      
      // If we have pickup/drop locations, fit bounds to include all points
      if (
        props?.orderPickupLocation &&
        props?.orderDropLocation &&
        isLatitude(props?.orderPickupLocation?.latitude) &&
        isLongitude(props?.orderPickupLocation?.longitude) &&
        isLatitude(props?.orderDropLocation?.latitude) &&
        isLongitude(props?.orderDropLocation?.longitude)
      ) {
        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend([robotLng, robotLat]);
        bounds.extend([props?.orderPickupLocation?.longitude, props?.orderPickupLocation?.latitude]);
        bounds.extend([props?.orderDropLocation?.longitude, props?.orderDropLocation?.latitude]);
        
        mapRef.current.fitBounds(bounds, {
          padding: 100,
          duration: 200,
        });
        console.log("Map fitted to bounds including robot, pickup, and drop locations");
      } else {
        // Just center on robot
        mapRef.current.setCenter([robotLng, robotLat]);
        mapRef.current.setZoom(15);
        console.log("Map centered on robot only");
      }
    }
    //  else {
    //   const bounds = new mapboxgl.LngLatBounds(
    //     props?.maxBoundCordinates?.southwest,
    //     props?.maxBoundCordinates?.northeast
    //   );

    //   bounds.extend([
    //     props?.orderDropLocation?.longitude || 0,
    //     props?.orderDropLocation?.latitude || 0
    //   ]);

    //   mapRef.current.fitBounds(bounds, {
    //     padding: 80,
    //     duration: 200,
    //   });
    // }
  }, [
    props,
    props?.orderDropLocation,
    props?.robotLocation,
    robotMarker,
    dropMarker,
    pickupMarker,
    dropLocationPopup,
    pickupLocationPopup,
    userControllingMap,
  ]);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
  
    const startControl = () => setUserControllingMap(true);
  
    // User drags or zooms or rotates the map
    map.on('dragstart', startControl);
    map.on('zoomstart', startControl);
    map.on('rotatestart', startControl);
  
    return () => {
      map.off('dragstart', startControl);
      map.off('zoomstart', startControl);
      map.off('rotatestart', startControl);
    };
  }, []);

  // Add a new useEffect to handle robot location updates
  useEffect(() => {
    console.log("Robot location update effect triggered:", props?.robotLocation);
    
    if (!mapRef.current || !props?.robotLocation) {
      console.log("Map not ready or no robot location data");
      return;
    }

    const robotLng = parseFloat(props?.robotLocation?.longitudeSlam || props?.robotLocation?.longitude || 0);
    const robotLat = parseFloat(props?.robotLocation?.latitudeSlam || props?.robotLocation?.latitude || 0);
    
    console.log("Processing robot location update:", robotLng, robotLat);
    console.log("Are coordinates valid?", isLongitude(robotLng), isLatitude(robotLat));
    
    if (isLongitude(robotLng) && isLatitude(robotLat)) {
      // If robot marker doesn't exist, create it
      if (!robotMarker) {
        console.log("Creating new robot marker");
        
        const robotMarkerDiv = document.createElement("div");
        robotMarkerDiv.style.width = "35px";
        robotMarkerDiv.style.height = "35px";
        robotMarkerDiv.style.borderRadius = "100%";
        robotMarkerDiv.style.backgroundImage = "url(/assets/images/robot-icon-2.svg)";
        robotMarkerDiv.style.backgroundSize = "contain";
        robotMarkerDiv.style.backgroundRepeat = "no-repeat";
        robotMarkerDiv.style.backgroundPosition = "center";
        robotMarkerDiv.style.backgroundColor = "rgba(255, 255, 255, 0.9)";
        robotMarkerDiv.style.border = "3px solid #00A16F";
        robotMarkerDiv.style.boxShadow = "0 0 10px rgba(0, 161, 111, 0.5)";
        
        const newRobotMarker = new mapboxgl.Marker(robotMarkerDiv)
          .setLngLat([robotLng, robotLat])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(
              `<div class="custom-popup">
                <p><strong>Robot: ${props?.robotLocation?.robotId || 'Unknown'}</strong></p>
                <p>Status: ${props?.robotLocation?.robotStatus || 'Unknown'}</p>
                <p>Coordinates: ${robotLat.toFixed(6)}, ${robotLng.toFixed(6)}</p>
              </div>`
            )
          )
          .addTo(mapRef.current);
        
        // Set robot heading/rotation if available
        if (!!Number(process.env.REACT_APP_HEADING) && (props?.robotLocation?.headingSlam || props?.robotLocation?.heading)) {
          const rotation = parseFloat(props?.robotLocation?.headingSlam || props?.robotLocation?.heading || 0);
          newRobotMarker.setRotation(rotation).setRotationAlignment("map");
          console.log("Robot marker rotation set to:", rotation);
        }
        
        setRobotMarker(newRobotMarker);
        console.log("Robot marker created and added to map");
        
        // Center the map on the robot
        mapRef.current.setCenter([robotLng, robotLat]);
        mapRef.current.setZoom(15);
        console.log("Map centered on robot");
      } else {
        // Update existing robot marker position
        console.log("Updating existing robot marker position");
        robotMarker.setLngLat([robotLng, robotLat]);
        
        // Update robot heading/rotation if available
        if (!!Number(process.env.REACT_APP_HEADING) && (props?.robotLocation?.headingSlam || props?.robotLocation?.heading)) {
          const rotation = parseFloat(props?.robotLocation?.headingSlam || props?.robotLocation?.heading || 0);
          robotMarker.setRotation(rotation).setRotationAlignment("map");
          console.log("Robot marker rotation updated to:", rotation);
        }
        
        // Update popup content
        robotMarker.getPopup().setHTML(
          `<div class="custom-popup">
            <p><strong>Robot: ${props?.robotLocation?.robotId || 'Unknown'}</strong></p>
            <p>Status: ${props?.robotLocation?.robotStatus || 'Unknown'}</p>
            <p>Coordinates: ${robotLat.toFixed(6)}, ${robotLng.toFixed(6)}</p>
          </div>`
        );
      }
    } else {
      console.log("Invalid robot coordinates - not updating marker");
    }
  }, [props?.robotLocation, robotMarker]);

  // Handle geofence polygon updates when geofence data changes
  useEffect(() => {
    if (mapRef.current) {
      console.log("Geofence data updated, refreshing polygon");
      addGeofencePolygon(mapRef.current);
    }
  }, [props?.geofenceData, addGeofencePolygon]);

  // Robot marker movement is now handled in the robot location update useEffect above

  return (
    <div style={{ height: "100%", position: "relative" }}>
      {userControllingMap &&
      !!Number(process.env.REACT_APP_USER_MAP_CONTROL) &&
      props?.robotLocation ? (
        <div
          className={`cursor-pointer flex items-center gap-2 sidebar bg-[#00B7D4] text-white p-[6px_12px] z-[1] absolute bottom-0 left-0 m-1 rounded-[4px] h-[30px]`}
          onClick={handleRecenter}
        >
          <FiNavigation2 strokeWidth={3} />
          <span className="text-xs font-bold uppercase">Re-centre</span>
        </div>
      ) : null}
      <div
        ref={mapContainerRef}
        id="map-container-id"
        className="map-container"
        style={{
          height: "100%",
          position: "absolute",
          width: "100%",
        }}
      ></div>
    </div>
  );
}

export default MapboxRobot;
